/**
 * Utility functions for data parsing and formatting
 */

import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Merge Tailwind CSS classes with clsx
 * This is the standard utility for conditionally joining class names
 * @param inputs - Class values to merge
 * @returns Merged class string
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Parse currency string to number
 * Handles: "$1,234.56", "€1.234,56", "1,234.56", etc.
 * @param value - Currency string or number
 * @returns Parsed number or 0 if invalid
 */
export const parseCurrency = (value: string | number | null | undefined): number => {
  if (typeof value === 'number') return value;
  if (!value) return 0;

  // Remove currency symbols and whitespace
  const cleaned = value
    .replace(/[$€£¥₹,\s]/g, '')
    .trim();

  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
};

/**
 * Parse percentage string to number
 * Handles: "85%", "85.5%", etc.
 * @param value - Percentage string or number
 * @returns Parsed number or 0 if invalid
 */
export const parsePercentage = (value: string | number | null | undefined): number => {
  if (typeof value === 'number') return value;
  if (!value) return 0;

  const cleaned = value.replace(/%/g, '').trim();
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
};

/**
 * Parse any numeric value safely
 * @param value - Any value that should be a number
 * @returns Parsed number or 0 if invalid
 */
export const parseNumber = (value: any): number => {
  if (typeof value === 'number' && !isNaN(value)) return value;
  if (!value) return 0;

  const parsed = parseFloat(String(value));
  return isNaN(parsed) ? 0 : parsed;
};

/**
 * Format number as currency
 * @param value - Number to format
 * @param currency - Currency code (USD, EUR, etc.)
 * @param locale - Locale for formatting
 */
export const formatCurrency = (
  value: number,
  currency: string = 'USD',
  locale: string = 'en-US'
): string => {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }).format(value);
};

/**
 * Format large numbers with abbreviated units (K, M, B, T)
 * @param value - Number to format
 * @param decimals - Number of decimal places (default: 1)
 * @returns Formatted string like "1.2M", "3.5B"
 */
export const formatLargeNumber = (value: number, decimals: number = 1): string => {
  if (value === 0) return '0';

  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';

  if (absValue >= 1e12) {
    return sign + (absValue / 1e12).toFixed(decimals) + 'T';
  }
  if (absValue >= 1e9) {
    return sign + (absValue / 1e9).toFixed(decimals) + 'B';
  }
  if (absValue >= 1e6) {
    return sign + (absValue / 1e6).toFixed(decimals) + 'M';
  }
  if (absValue >= 1e3) {
    return sign + (absValue / 1e3).toFixed(decimals) + 'K';
  }

  return sign + absValue.toFixed(decimals);
};

/**
 * Format large currency values with abbreviated units
 * @param value - Number to format
 * @param currency - Currency symbol (default: '$')
 * @param decimals - Number of decimal places (default: 1)
 * @returns Formatted string like "$1.2M", "$3.5B"
 */
export const formatLargeCurrency = (
  value: number,
  currency: string = '$',
  decimals: number = 1
): string => {
  const formatted = formatLargeNumber(value, decimals);
  return currency + formatted;
};

/**
 * Format percentage with specified decimal places
 * @param value - Decimal value (0.85 for 85%)
 * @param decimals - Number of decimal places (default: 0)
 * @returns Formatted percentage string like "85%"
 */
export const formatPercentage = (value: number, decimals: number = 0): string => {
  return (value * 100).toFixed(decimals) + '%';
};

/**
 * Safely divide two numbers, return 0 if denominator is 0
 */
export const safeDivide = (numerator: number, denominator: number): number => {
  return denominator === 0 ? 0 : numerator / denominator;
};

/**
 * Calculate percentage with safe division
 */
export const calculatePercentage = (part: number, total: number): number => {
  return safeDivide(part * 100, total);
};
